import { usePathHelper } from '@/hooks/usePathHelper'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { Can, ReadOnly, useAbility, useOptionalIsReadOnly } from '@/lib/casl'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { useAppForm } from '@/lib/form'
import { resolveOrganizationDeletionRedirectId } from '@/lib/organization-switch'
import {
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useOrganizationRepresentatives,
  useUpdateOrganizationMutation,
} from '@/lib/services/organizations/organizations.query'
import { canEditOrganization } from '@/lib/services/organizations/organizations.access'
import { isSalesDemoProvisioning } from '@/lib/services/sales-demos/sales-demos.utils'
import { cn, enumToSelectOptions, getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { CircleDollarSign, Phone } from 'lucide-react'
import { useId } from 'react'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Typography } from '@/components/ui/typography'
import SaveBar from './SaveBar'
import DeleteButton from './DeleteButton'

const emptyOrganization = {
  name: '',
  type: OrganizationType.Florist.Value,
  sales_rep_id: '',
  support_rep_id: '',
  fsn_client_id: '',
  google_verification_token: '',
}

const organizationFormDefaults = (organization) => ({
  ...emptyOrganization,
  ...organization,
  sales_rep_id: organization?.sales_rep_id ?? '',
  support_rep_id: organization?.support_rep_id ?? '',
  fsn_client_id: organization?.fsn_client_id ?? '',
  google_verification_token: organization?.google_verification_token ?? '',
})

const typeOptions = enumToSelectOptions(OrganizationType, {
  includeIcons: true,
})?.filter((opt) => opt.id !== OrganizationType.Global.Value)

function buildRepOptions(initialData, users = []) {
  const reps = [...users, initialData?.sales_rep, initialData?.support_rep].filter(Boolean)
  const optionsById = new Map()

  reps.forEach((rep) => {
    const key = String(rep.id)

    if (!optionsById.has(key)) {
      optionsById.set(key, rep)
    }
  })

  return [...optionsById.values()]
}

export default function OrganizationForm({
  initialData,
  beforeDeleteContent = null,
  className = 'max-w-4xl',
  salesDemo,
}) {
  const ability = useAbility()
  const id = useId()
  const { isReadOnly } = useOptionalIsReadOnly()
  const isEdittingGlobalOrg = initialData?.type === OrganizationType.Global.Value
  const organizationTypeOptions = isEdittingGlobalOrg
    ? enumToSelectOptions(OrganizationType, { includeIcons: true })
    : typeOptions
  const canEditInitialOrganization = initialData
    ? canEditOrganization(ability, initialData)
    : ability.can('create', 'organizations')
  const currentSalesDemo = salesDemo === undefined ? initialData?.sales_demo : salesDemo
  const isPreparingSalesDemo = isSalesDemoProvisioning(currentSalesDemo)
  const canAccessGlobal = ability.can('access', 'global')
  const canLoadGlobalRepUsers = canAccessGlobal && !isReadOnly && canEditInitialOrganization
  const { data: users = [], isPending: areUsersPending } = useOrganizationRepresentatives({
    enabled: canLoadGlobalRepUsers,
  })
  const repUsersArePending = canLoadGlobalRepUsers && areUsersPending
  const repOptions = buildRepOptions(initialData, users)
  const navigate = useNavigate()
  const { organizations } = usePathHelper()
  const { isImpersonating, organization: currentOrganization, user } = useOrganizationContext()
  const createMutation = useCreateOrganizationMutation()
  const updateMutation = useUpdateOrganizationMutation()
  const deleteMutation = useDeleteOrganizationMutation({
    cleanupOnSuccess: false,
  })
  const deleteRedirectOrganizationId = resolveOrganizationDeletionRedirectId({
    deletedOrganizationId: initialData?.id,
    currentOrganizationId: currentOrganization?.id,
    isImpersonating,
    userOrganizationId: user?.organization_id,
  })
  const deleteDestination = deleteRedirectOrganizationId
    ? organizations.index().using({ orgId: deleteRedirectOrganizationId })
    : organizations.index()

  const form = useAppForm({
    defaultValues: organizationFormDefaults(initialData),
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          if (initialData) {
            await updateMutation.mutateAsync({ id: initialData.id, ...value })
            toast.success('Organization updated successfully')
            setTimeout(() => formApi.reset())
          } else {
            const { organization_id } = await createMutation.mutateAsync(value)
            const to = organization_id
              ? organizations.show(organization_id).link
              : organizations.index().link
            toast.success('Organization created successfully')
            return navigate({ ...to, ignoreBlocker: true })
          }
        } catch (error) {
          toast.error(getErrorMessage(error))
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <div className={cn(className)}>
      <ReadOnly isReadOnly={Boolean(initialData) && !canEditInitialOrganization}>
        <form.AppForm>
          <form.Form>
            <div className="grid gap-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Typography variant="h4">Organization Details</Typography>
                  </CardTitle>
                  <CardDescription>
                    Enter the basic information about the organization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-y-6">
                    <form.AppField name="name">
                      {(field) => <field.TextInput id={`${id}-name`} label="Name" />}
                    </form.AppField>
                    <form.AppField name="type">
                      {(field) => (
                        <field.SelectInput
                          id={`${id}-type`}
                          label="Type"
                          options={organizationTypeOptions}
                          disabled={Boolean(initialData)}
                        />
                      )}
                    </form.AppField>
                    <Can I="access" the="global">
                      {!repUsersArePending ? (
                        <>
                          <form.AppField name="sales_rep_id">
                            {(field) => (
                              <field.SelectInput
                                id={`${id}-sales-rep`}
                                label={
                                  <>
                                    <CircleDollarSign size={16} /> Sales Rep
                                  </>
                                }
                                options={repOptions}
                              />
                            )}
                          </form.AppField>
                          <form.AppField name="support_rep_id">
                            {(field) => (
                              <field.SelectInput
                                id={`${id}-support-rep`}
                                label={
                                  <>
                                    <Phone size={16} /> Support Rep
                                  </>
                                }
                                options={repOptions}
                              />
                            )}
                          </form.AppField>
                        </>
                      ) : null}
                      <form.AppField name="fsn_client_id">
                        {(field) => (
                          <field.TextInput
                            id={`${id}-fsn-client-id`}
                            label="FSN Client ID"
                            inputMode="numeric"
                          />
                        )}
                      </form.AppField>
                    </Can>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Typography variant="h4">Google Verification Token</Typography>
                  </CardTitle>
                  <CardDescription>
                    Enter the Google verification token to verify the organization's ownership of
                    the domain
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-y-6">
                    <form.AppField name="google_verification_token">
                      {(field) => (
                        <field.TextInput id={`${id}-google-verification-token`} label="Token" />
                      )}
                    </form.AppField>
                  </div>
                </CardContent>
              </Card>
            </div>
            {canEditInitialOrganization ? <SaveBar /> : null}
          </form.Form>
        </form.AppForm>
        {beforeDeleteContent ? <div className="mt-6">{beforeDeleteContent}</div> : null}
        {!!initialData && !isPreparingSalesDemo && (
          <Can do="delete" on="organizations">
            <DeleteButton
              id={initialData.id}
              model="Organization"
              mutation={deleteMutation}
              path={deleteDestination}
              disabled={isEdittingGlobalOrg}
              replace
              onDeleted={deleteMutation.cleanupDeletedOrganization}
            />
          </Can>
        )}
      </ReadOnly>
    </div>
  )
}
